<template>
  <div class="lesson-text-box">
    <VLessonPlaceholder v-if="isEmptyBlock" />
    
    <div v-else class="w-full h-full m-0 box-border transition-all relative main-group"
         :style="[
          CssStyleHelpers.background(
            fields.mainGroup?.backgroundColor,
            fields.mainGroup?.isShowBackgroundColor
          ),
          CssStyleHelpers.borderRadius(fields.mainGroup?.borderRadius),
          CssStyleHelpers.paddings(fields.mainGroup?.paddings),
          CssStyleHelpers.margins(fields.mainGroup?.margins),
          {
            '--main-padding-top-mobile': `${fields.mainGroup?.paddingsMobile?.top ?? fields.mainGroup?.paddings?.top ?? 0}px`,
            '--main-padding-right-mobile': `${fields.mainGroup?.paddingsMobile?.right ?? fields.mainGroup?.paddings?.right ?? 0}px`,
            '--main-padding-bottom-mobile': `${fields.mainGroup?.paddingsMobile?.bottom ?? fields.mainGroup?.paddings?.bottom ?? 0}px`,
            '--main-padding-left-mobile': `${fields.mainGroup?.paddingsMobile?.left ?? fields.mainGroup?.paddings?.left ?? 0}px`,
          },
        ]">
      <div class="w-full h-full flex"
           :style="[CssStyleHelpers.flexDirection(fields.mainGroup?.gridReverse)]">
        <!-- Медиа контент -->
        <div v-if="fields.mainGroup?.isShowBackgroundMedia"
             class="media-background w-full overflow-hidden"
             :style="[
              CssStyleHelpers.borderRadius(fields.mainGroup?.borderRadius),
            ]">
          <MediaCard fill
                     :media-type="fields.mainGroup?.backgroundMedia?.type"
                     :image-props="CssImageHelpers.imageProps(fields.mainGroup?.backgroundMedia)"
                     :video-props="CssVideoHelpers.videoProps(fields.mainGroup?.backgroundMedia)"
                     :mask-props="fields.mainGroup?.backgroundMediaMask"
                     :object-fit="fields.mainGroup?.objectFit || 'cover'" />
        </div>

        <!-- Карточки -->
        <div class="w-full h-full">
          <div v-if="fields.titleGroup?.isShown && fields.titleGroup?.title"
               class="w-full  pb-0 relative z-10">
            <TextCard
              class="lesson-title"
              :style="[
                CssStyleHelpers.margins({
                  bottom: fields.titleGroup?.spacing,
                }),
              ]"
              :text="fields.titleGroup?.title || ''"
            />
          </div>

          <div v-if="fields.textGroup?.isShown && fields.textGroup?.text"
               class="w-full pb-0 relative z-10">
            <TextCard
              class="lesson-text"
              :style="[
                CssStyleHelpers.margins({
                  bottom: fields.textGroup?.spacing,
                }),
              ]"
              :text="fields.textGroup?.text || ''"
            />
          </div>

          <!-- Single Choice Test -->
          <SingleChoiceTest
            v-if="testType === TestType.SINGLE_CHOICE"
            :cards="fields.cardsGroup?.cardsCollection"
            :cards-group="fields.cardsGroup"
            :main-group="fields.mainGroup"
            :true-answers="trueAnswers"
            :user-answers="userAnswers"
            :show-answers="showAnswers"
            @update:userAnswers="handleUserAnswers"
          />
          
          <!-- Multiple Choice Test -->
          <MultipleChoiceTest
            v-else-if="testType === TestType.MULTIPLE_CHOICE"
            :cards="fields.cardsGroup?.cardsCollection"
            :cards-group="fields.cardsGroup"
            :main-group="fields.mainGroup"
            :true-answers="trueAnswers"
            :user-answers="userAnswers"
            :show-answers="showAnswers"
            @update:userAnswers="handleUserAnswers"
          />
          
          <!-- User Input Test -->
          <UserInputTest
            v-else-if="testType === TestType.USER_INPUT"
            :cards-group="fields.cardsGroup"
            :main-group="fields.mainGroup"
            :true-answers="trueAnswers"
            :user-answers="userAnswers"
            :show-answers="showAnswers"
            @update:userAnswers="handleUserAnswers"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, PropType} from "vue";
import MediaCard from "../cards/MediaCard.vue";
import SingleChoiceTest from "../cards/test-types/SingleChoiceTest.vue";
import MultipleChoiceTest from "../cards/test-types/MultipleChoiceTest.vue";
import UserInputTest from "../cards/test-types/UserInputTest.vue";
import TextCard from "../cards/TextCard.vue";
import VLessonPlaceholder from "../VLessonPlaceholder.vue";
import {ILayoutComponentData} from "../types/ILayoutFieldData";
import {CssImageHelpers, CssStyleHelpers, CssVideoHelpers} from "../utils/cssStyleHelpers";
import {useDataFields} from "../composables/data";
import {TestType} from "../types/fields.enum";
import {htmlToText} from "../utils/html/html";
const props = defineProps({
  modelValue: {
    type: Object as PropType<ILayoutComponentData | undefined>,
    required: true,
  },
  mode: {
    type: String as PropType<"view" | "edit">,
    default: "view",
  },
  showAnswers: {
    type: Boolean,
    default: undefined,
  },
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: ILayoutComponentData): void
}>();

// Получение данных теста из exposedData
const testType = computed(() => {
  const exposed = props.modelValue?.exposedData as Record<string, any> | undefined;
  return exposed?.testType || TestType.SINGLE_CHOICE;
});

const trueAnswers = computed(() => {
  const exposed = props.modelValue?.exposedData as Record<string, any> | undefined;
  const answers = exposed?.trueAnswers || [];
  // Для USER_INPUT возвращаем как есть (массив строк), для других типов - массив чисел
  if (testType.value === TestType.USER_INPUT) {
    return answers;
  }
  return Array.isArray(answers) ? answers.map(Number).filter(n => !isNaN(n)) : [];
});

const trueAnswersStrings = computed(() => {
  if (testType.value !== TestType.USER_INPUT) return [];
  const exposed = props.modelValue?.exposedData as Record<string, any> | undefined;
  const answers = exposed?.trueAnswers || [];
  return Array.isArray(answers) ? answers.map(String) : [];
});

// Відповідь учня може приїхати або в exposedData.userAnswers (клієнтська гідрація),
// або тільки верхнім рівнем — userAnswers / user_answers (серверна гідрація studying_results).
const rawUserAnswers = computed(() => {
  const element = props.modelValue as Record<string, any> | undefined;
  const exposed = element?.exposedData as Record<string, any> | undefined;
  return exposed?.userAnswers ?? element?.userAnswers ?? element?.user_answers;
});

const userAnswers = computed(() => {
  // Для USER_INPUT віддаємо як є: undefined = зовнішнього значення немає (не плутати
  // з "порожня відповідь"), інакше кожен ререндер створював би новий [] і стирав поле.
  if (testType.value === TestType.USER_INPUT) {
    return rawUserAnswers.value;
  }
  const answers = rawUserAnswers.value || [];
  return Array.isArray(answers) ? answers.map(Number).filter(n => !isNaN(n)) : [];
});

const userAnswersStrings = computed(() => {
  if (testType.value !== TestType.USER_INPUT) return [];
  const answers = rawUserAnswers.value;
  // undefined замість [] — щоб UserInputTest відрізняв "немає даних" від "порожня відповідь"
  return Array.isArray(answers) ? answers.map(String) : undefined;
});

const isEditMode = computed(() => props.mode === "edit");

const showAnswers = computed(() => props.showAnswers === true);

// Функція для застосування дефолтних значень відступів
const applyDefaultMainPaddings = (paddings?: Record<string, number>) => ({
  top: paddings?.top ?? 0,
  right: paddings?.right ?? 0,
  bottom: paddings?.bottom ?? 0,
  left: paddings?.left ?? 0,
  ...paddings,
});

const applyDefaultMainMargins = (margins?: Record<string, number>) => ({
  top: margins?.top ?? 50,
  right: margins?.right ?? 0,
  bottom: margins?.bottom ?? 0,
  left: margins?.left ?? 0,
  ...margins,
});

// Используем данные напрямую из newData
const fields = computed(() => {
  const newData = props.modelValue?.data;
  if (!newData) return {titleGroup: {}, textGroup: {}, cardsGroup: {}, mainGroup: {}};

  const {getData} = useDataFields(newData);

  return {
    titleGroup: {
      title: getData("titleGroup.title")?.text,
      isShown: getData("titleGroup.isShown")?.value,
      spacing: getData("titleGroup.spacing")?.value || 0,
    },
    textGroup: {
      text: getData("textGroup.text")?.text,
      isShown: getData("textGroup.isShown")?.value,
      spacing: getData("textGroup.spacing")?.value || 0,
    },
    mainGroup: {
      gridReverse: getData("mainGroup.gridReverse")?.value,
      isShowCardMedia: getData("mainGroup.isShowCardMedia")?.value,
      isShowCardText: getData("mainGroup.isShowCardText")?.value,
      isShowBackgroundColor: getData("mainGroup.isShowBackgroundColor")?.value,
      isShowBackgroundMedia: getData("mainGroup.isShowBackgroundMedia")?.value,
      backgroundColor: getData("mainGroup.backgroundColor")?.value,
      borderRadius: getData("mainGroup.borderRadius")?.value,
      backgroundMedia: getData("mainGroup.backgroundMedia"),
      objectFit: getData("mainGroup.backgroundMedia")?.objectFit ?? 'contain',
      backgroundMediaMask: getData("mainGroup.backgroundMediaMask"),
      paddings: applyDefaultMainPaddings(getData("mainGroup.paddings")),
      paddingsMobile: applyDefaultMainPaddings(
        getData("mainGroup.paddingsMobile") || getData("mainGroup.paddings")
      ),
      margins: applyDefaultMainMargins(getData("mainGroup.margins")),
    },
    // Все данные и стили для карточек
    cardsGroup: {
      // Основные данные карточек
      cardsCollection: getData("cardsGroup.cards")?.cardsCollection,

      // Стили контейнера карточек (внешние отступы, размытие, фон)
      borderRadius: getData("cardsGroup.cardsBorderRadius")?.value,
      cardsBetweenSpacing: getData("cardsGroup.cardsBetweenSpacing")?.value,
      cardsOuterSpacing: getData("cardsGroup.cardsOuterSpacing"),
      cardTextSpacing: getData("cardsGroup.cardTextSpacing"),
      cardsBorderRadius: getData("cardsGroup.cardsBorderRadius")?.value,
      textBorderRadius: getData("cardsGroup.textBorderRadius")?.value,
      columns: getData("cardsGroup.columns"),
      reverseCheckboxOrder: getData("cardsGroup.reverseCheckboxOrder")?.value,
      // Стили контента карточек (внутренний фон)
      isCardBackgroundShown: getData("cardsGroup.isShowCardBackground")?.value,
      cardBackgroundColor: getData("cardsGroup.cardBackgroundColor")?.value,
      isTextBackgroundShown: getData("cardsGroup.isShowCardText")?.value,
      textBackgroundColor: getData("cardsGroup.textBackgroundColor")?.value,

      // Поля відображення передаємо з mainGroup для сумісності з тестовими компонентами
      isShowCardMedia: getData("mainGroup.isShowCardMedia")?.value,
      isShowTextMedia: getData("mainGroup.isShowCardText")?.value,
      isShowBackgroundMedia: getData("mainGroup.isShowBackgroundMedia")?.value,
      // Настройки медиа
      imageProps: CssImageHelpers.imageProps(getData("mainGroup.backgroundMedia")),
      videoProps: CssVideoHelpers.videoProps(getData("mainGroup.backgroundMedia")),
      maskProps: getData("mainGroup.backgroundMediaMask"),
      // Тінь для карток
      isShowShadow: getData("cardsGroup.isShowShadow")?.value,
      // Опції стилів карток
      isShowCardPadding: getData("cardsGroup.isShowCardPadding")?.value,
      isShowCardRounded: getData("cardsGroup.isShowCardRounded")?.value,
      isShowCardWhiteBackground: getData("cardsGroup.isShowCardWhiteBackground")?.value,
      // Кольори чекбоксів та радіобатонів
      checkboxRadioColor: getData("cardsGroup.checkboxRadioColor")?.value || '#ffffff',
      checkboxRadioBorderColor: getData("cardsGroup.checkboxRadioBorderColor")?.value || '#CFCFCF',
    },
  };
});

function handleUserAnswers(userAnswers: any[]) {
  if (!props.modelValue) return;

  const updatedData = {
    ...props.modelValue,
    exposedData: {
      ...props.modelValue?.exposedData,
      options: fields.value?.cardsGroup?.cardsCollection?.map((card: any) => htmlToText(card.textField?.data?.text)),
      trueAnswers: testType.value === TestType.USER_INPUT
        ? trueAnswers.value.map((answer: string) => htmlToText(answer))
        : trueAnswers.value,
      showAnswers: showAnswers.value,
      userAnswers: userAnswers,
    },
  } as ILayoutComponentData

  emit("update:modelValue", updatedData);
}

// Перевіряємо чи блок пустий
const isEmptyBlock = computed(() => {
  const hasCards = fields.value?.cardsGroup?.cardsCollection && fields.value.cardsGroup.cardsCollection.length > 0;
  const hasTitle = fields.value?.titleGroup?.isShown && fields.value?.titleGroup?.title && fields.value.titleGroup.title.trim() !== '';
  const hasText = fields.value?.textGroup?.isShown && fields.value?.textGroup?.text && fields.value.textGroup.text.trim() !== '';
  // Проверяем медиа - должно быть включено и иметь данные
  const hasMedia = fields.value?.mainGroup?.isShowBackgroundMedia && fields.value?.mainGroup?.backgroundMedia && (
    fields.value.mainGroup.backgroundMedia.previewUrl || 
    fields.value.mainGroup.backgroundMedia.url ||
    fields.value.mainGroup.backgroundMedia.type
  );
  const hasColor = fields.value?.mainGroup?.isShowBackgroundColor && fields.value?.mainGroup?.backgroundColor;
  
  return !(hasCards || hasTitle || hasText || hasMedia || hasColor);
});

</script>
<style scoped>
@reference "tailwindcss";
/* Основные стили для контейнера */
.lesson-text-box {
  @apply w-full flex flex-col gap-0;
  container-type: inline-size;
  /* pointer-events: none; */
}

/* Стили для заголовка */
.lesson-title {
  @apply mb-4 relative z-10;
  color: var(--text-primary);
  /* background: rgba(255, 255, 255, 0.9); */
  /* padding: 0.75rem 1rem; */
  border-radius: 0.5rem;
  /* backdrop-filter: blur(2px); */
  display: inline-block;
}

/* Стили для текста */
.lesson-text {
  @apply text-base leading-relaxed m-0 p-0 mb-6;
  color: var(--text-primary);
}

/* Стили для медиа фона */
.media-background {
  @apply w-full h-full absolute inset-0 z-0;
  border-radius: inherit;
  overflow: hidden;
}

.media-background :deep(.media-card) {
  height: 100%;
}

/* Override @container height:auto !important from MediaContainerLayer and MaskLayer —
   those resets are correct for inline media but must not apply to the background fill. */
.media-background :deep(.media-container-layer),
.media-background :deep(.media-container-layer > div) {
  height: 100% !important;
  min-height: unset !important;
  max-height: unset !important;
  aspect-ratio: unset !important;
}

.media-background :deep(img),
.media-background :deep(video) {
  @apply w-full h-full object-cover;
}

/* Стили для текста поверх медиа */
.card-item {
  @apply text-base leading-relaxed relative z-10;
  /* color: var(--text-primary); */
  /* background: rgba(255, 255, 255, 0.2); */
}

/* Стили для заглушки */


.placeholder-icon {
  @apply text-5xl text-gray-500 mb-4 opacity-70;
}

.placeholder-text h3 {
  @apply text-lg font-semibold text-gray-700 m-0 mb-2;
}

.placeholder-text p {
  @apply text-sm text-gray-500 m-0 leading-relaxed;
}

/* Адаптивность для мобильных устройств */
@container (max-width: 700px) {
  .main-group {
    padding-top: var(--main-padding-top-mobile) !important;
    padding-right: var(--main-padding-right-mobile) !important;
    padding-bottom: var(--main-padding-bottom-mobile) !important;
    padding-left: var(--main-padding-left-mobile) !important;
  }

  .lesson-text {
    @apply text-base;
  }

  .lesson-title {
    @apply text-base;
  }
}

</style>
<style>
@import "../styles/dialogs-styles.css";
</style>
